import React from 'react'

import { Box, InfoDot, Tag, Text } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import { CompensationMethod } from 'modules/attendance/components/ClockIn/EmployeeTimeTrackingBreakdown/Compensation/types'
import MinutesToText from 'modules/attendance/components/MinutesToText'
import { useTimesheetData } from 'modules/attendance/lib/timesheetDataContext'
import { TimesheetFeatures } from 'modules/attendance/lib/timesheetFeatures'
import { UniqueTimeBlockData } from 'modules/attendance/store/models/UniqueTimeBlockData'
import { CustomTimeRangeCategory } from 'modules/time_categories_settings/hooks/useFetchTimeSettingsCustomTimeRangeCategories'

import CategoryItem from './CategoryItem'

interface Props {
  compensationMethod: CompensationMethod
  transfers: UniqueTimeBlockData[]
  discardedTransfers?: UniqueTimeBlockData[]
  totalTransfersByPoolType: number
  compensated: boolean
  timeSettingsCategoryById: Record<number, CustomTimeRangeCategory[]>
}

function getCompensatedCopy(compensationMethod: CompensationMethod, compensated: boolean) {
  if (compensationMethod === 'payroll' && compensated) {
    return i18n.t('clock_in.sidepanel.compensation.sent_to_payroll')
  } else if (compensated) {
    return i18n.t('clock_in.sidepanel.compensation.compensated')
  } else {
    return i18n.t('clock_in.sidepanel.compensation.pending')
  }
}

function getCompensatedColor(compensationMethod: CompensationMethod, compensated: boolean) {
  if (compensationMethod === 'payroll') {
    return compensated ? 'purple1000' : 'purple200'
  } else if (compensationMethod === 'timeoff') {
    return 'champagne1000'
  } else {
    return 'purple200'
  }
}

const Card = ({
  compensationMethod,
  transfers,
  discardedTransfers = [],
  totalTransfersByPoolType,
  compensated,
  timeSettingsCategoryById,
}: Props) => {
  const { featureByName } = useTimesheetData()

  const compensatedCopy = getCompensatedCopy(compensationMethod, compensated)
  const infoDotColor = getCompensatedColor(compensationMethod, compensated)
  const hasExtraHourRules = TimesheetFeatures.EXTRA_HOUR_RULES in featureByName

  return (
    <Box
      paddingY="s16"
      paddingX="s24"
      gap="s12"
      borderRadius={{ all: 'abs008' }}
      background={compensated ? 'grey100' : undefined}
      border={{
        all: {
          width: 's1',
          color: 'grey400',
          style: 'solid',
        },
      }}
    >
      <Box alignSelf="flexStart">
        <Tag type={compensated ? 'correct' : 'champagne'}>{compensatedCopy}</Tag>
      </Box>
      <Box flexDirection="row" justifyContent="spaceBetween">
        <Box flexDirection="row" gap="s8">
          <InfoDot size="medium" color={infoDotColor} />
          <Text color="grey800" size="100" weight="bold" caps>
            {compensationMethod === 'timeoff'
              ? i18n.t('clock_in.sidepanel.compensation.with_time_off')
              : i18n.t('clock_in.sidepanel.compensation.with_payroll')}
          </Text>
        </Box>
        <MinutesToText
          color="grey800"
          value={totalTransfersByPoolType}
          size="100"
          weight="bold"
          caps
        />
      </Box>
      {hasExtraHourRules ? (
        <Box gap="s6">
          {transfers.map((transfer, index) => (
            <CategoryItem
              key={index}
              transfer={transfer}
              timeSettingsCategoryById={timeSettingsCategoryById}
            />
          ))}
        </Box>
      ) : (
        transfers.map((transfer, index) => (
          <CategoryItem
            key={index}
            transfer={transfer}
            timeSettingsCategoryById={timeSettingsCategoryById}
          />
        ))
      )}
      {discardedTransfers.map((transfer, index) => (
        <CategoryItem
          key={index}
          transfer={transfer}
          timeSettingsCategoryById={timeSettingsCategoryById}
        />
      ))}
      {hasExtraHourRules && compensationMethod === 'payroll' && (
        <Box>
          <Text size="200" color="grey1000">
            {i18n.t('clock_in.sidepanel.compensation.payroll_footer')}
          </Text>
        </Box>
      )}
    </Box>
  )
}

export default Card
